import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of, map, catchError } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = environment.api+'/auth';
  private authStateSubject = new BehaviorSubject<boolean>(false);
  private userEmailSubject = new BehaviorSubject<string>('');
  private userNameSubject = new BehaviorSubject<string>('');

  // Observables públicos para que otros componentes puedan suscribirse
  public authState$ = this.authStateSubject.asObservable();
  public userEmail$ = this.userEmailSubject.asObservable();
  public userName$ = this.userNameSubject.asObservable();

  constructor(private http: HttpClient) {
    // Inicializar el estado de autenticación al crear el servicio
    this.checkInitialAuthState();
  }

  createUser(userData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/register`, userData);
  }
  isAuthenticatedAndValid() : Observable<any>{
    const token = this.getFullToken();
    if (!token) return of({ valid: false });
    return this.http.get(`${this.apiUrl}/verify-token`, { headers: { Authorization: token } }).pipe(
      map(() => ({ valid: true })),
      catchError(err => {
        if (err.status === 401) {
          this.logout();
          return of({ valid: false });
        }
        return of({ valid: false });
      })
    );
  }

  login(credentials: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/login`, credentials);
  }

  requestPasswordReset(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/request-password-reset`, { email });
  }

  resetPassword(resetData: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/reset-password`, resetData);
  }

  // Método para almacenar el token en localStorage
  setToken(accessToken: string, tokenType: string = 'Bearer'): void {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('token_type', tokenType);
    this.updateAuthState();
  }

  // Método para obtener el token del localStorage
  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  // Método para obtener el tipo de token del localStorage
  getTokenType(): string | null {
    return localStorage.getItem('token_type');
  }

  // Método para obtener el token completo con el tipo (ej: "Bearer token123")
  getFullToken(): string | null {
    const token = this.getToken();
    const tokenType = this.getTokenType();
    
    if (token && tokenType) {
      return `${tokenType} ${token}`;
    }
    
    return token;
  }

  // Método para verificar si el usuario está autenticado
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  

  // Método para cerrar sesión y limpiar el localStorage
  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user_data');
    this.updateAuthState();
  }

  // Método privado para verificar el estado inicial de autenticación
  private checkInitialAuthState(): void {
    this.updateAuthState();
  }

  // Método privado para actualizar el estado de autenticación
  private updateAuthState(): void {
    const isAuth = this.isAuthenticated();
    this.authStateSubject.next(isAuth);
    
    if (isAuth) {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          this.userEmailSubject.next(user.email || '');
          this.userNameSubject.next(user.name || '');
        } catch (error) {
          this.userEmailSubject.next('');
          this.userNameSubject.next('');
        }
      } else {
        this.userEmailSubject.next('');
        this.userNameSubject.next('');
      }
    } else {
      this.userEmailSubject.next('');
      this.userNameSubject.next('');
    }
  }

  // Método público para actualizar el email del usuario
  setUserEmail(email: string, name: string): void {
    localStorage.setItem('user_data', JSON.stringify({ email, name }));
    this.userEmailSubject.next(email);
    this.userNameSubject.next(name);
  }

  // Método para obtener el nombre completo del usuario
  getUserName(): string {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user.name || '';
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    return '';
  }

  // Método para obtener solo el primer nombre del usuario
  getFirstName(): string {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const fullName = user.name || '';
        // Dividir por espacios y tomar solo la primera parte
        return fullName.split(' ')[0] || '';
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    return '';
  }

  // Método para obtener las iniciales del usuario
  getUserInitials(): string {
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const name = user.name || '';
        if (name) {
          const names = name.trim().split(' ');
          if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase();
          } else if (names.length === 1) {
            return names[0][0].toUpperCase();
          }
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    return 'U'; // Default fallback
  }
}
